/**
 * Dashboard iPad - Clean Token Watchlist
 *
 * A minimalist dashboard designed for iPad wallpaper use.
 * Displays tokens with real-time prices and performance from cost basis.
 * Data is synced to Supabase for authenticated users.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCoinPrices, useSearchCoins, useCoinsList } from '../hooks/queries/useCoinGecko';
import { useIpadWatchlistSync } from '../hooks/useIpadWatchlistSync';
import type { WatchlistToken, TokenAlert, AlertConditionType } from '../hooks/useIpadWatchlistSync';
import { StorageService } from '../services/storage';  // Still used for portfolio loading
import { Icons } from '../components/Icons';
import { useToast } from '../components/Toast';
import { useAuthStore } from '../store/authStore';
import type { Token, Prices } from '../types';

// Predefined colors for alerts
const ALERT_COLORS = [
  { id: 'white', label: 'Blanc', color: '#ffffff' },
  { id: 'green', label: 'Vert', color: '#10b981' },
  { id: 'red', label: 'Rouge', color: '#ef4444' },
  { id: 'blue', label: 'Bleu', color: '#3b82f6' },
  { id: 'purple', label: 'Violet', color: '#8b5cf6' },
  { id: 'orange', label: 'Orange', color: '#f97316' },
];

// Generate synthetic sparkline data based on 24h change (deterministic based on seed)
function generateSparklineData(change24h: number, seed: string, points: number = 24): number[] {
  const data: number[] = [];
  const trend = change24h / 100; // Convert percentage to decimal
  const volatility = Math.abs(trend) * 0.3 + 0.015; // Moderate volatility

  // Simple seeded random function for deterministic results
  const seedNum = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const seededRandom = (i: number) => {
    const x = Math.sin(seedNum + i * 9999) * 10000;
    return x - Math.floor(x);
  };

  for (let i = 0; i < points; i++) {
    const progress = i / (points - 1);
    const trendComponent = trend * progress * 100;
    const randomComponent = (seededRandom(i) - 0.5) * volatility * 100;
    const value = 100 + trendComponent + randomComponent * (1 - progress * 0.6);
    data.push(value);
  }

  // Ensure last point reflects actual change
  data[data.length - 1] = 100 + change24h;
  return data;
}

// Mini Sparkline SVG component
function MiniSparkline({ data, color, opacity = 0.15, id }: { data: number[]; color: string; opacity?: number; id?: string }) {
  if (!data || data.length < 2) return null;

  const width = 200;
  const height = 80;
  const minY = Math.min(...data);
  const maxY = Math.max(...data);
  const range = maxY - minY || 1;

  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = height - ((value - minY) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  const fillPath = `M 0,${height} L ${points.split(' ').map(p => p).join(' L ')} L ${width},${height} Z`;
  const gradientId = `sparkGrad-${id || Math.random().toString(36).substr(2, 9)}`;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        opacity,
        pointerEvents: 'none',
      }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradientId})`} />
    </svg>
  );
}

export function DashboardIpad() {
  const toast = useToast();
  const { isAuthenticated } = useAuthStore();

  // Use synced watchlist hook (syncs to Supabase for authenticated users)
  const {
    config,
    setConfig,
    isLoading: isLoadingConfig,
    isSyncing,
    lastSyncedAt,
    syncError,
  } = useIpadWatchlistSync();

  const [showSettings, setShowSettings] = useState(false);
  const [showAddToken, setShowAddToken] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [editingCostBasis, setEditingCostBasis] = useState<string | null>(null);
  const [tempCostBasis, setTempCostBasis] = useState('');
  const [editingAlerts, setEditingAlerts] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hoveredToken, setHoveredToken] = useState<string | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  // Pagination
  const tokensPerPage = config.gridCols * config.rowsPerPage;
  const totalPages = Math.ceil(config.tokens.length / tokensPerPage);
  const paginatedTokens = useMemo(() => {
    const start = currentPage * tokensPerPage;
    return config.tokens.slice(start, start + tokensPerPage);
  }, [config.tokens, currentPage, tokensPerPage]);

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [currentPage, totalPages]);

  // Portfolio tokens for cost basis
  const [portfolioTokens, setPortfolioTokens] = useState<Token[]>([]);

  useEffect(() => {
    interface Portfolio { id: string; name: string; tokens: Token[]; }
    const portfolios = StorageService.get<Portfolio[]>('portfolios', []);
    const activePortfolioId = StorageService.get<string>('active_portfolio_id', 'default');
    const activePortfolio = portfolios.find(p => p.id === activePortfolioId) || portfolios[0];
    setPortfolioTokens(activePortfolio?.tokens || []);
  }, []);

  const coinIds = useMemo(() => config.tokens.map(t => t.id), [config.tokens]);

  const { data: prices = {}, isLoading, refetch } = useCoinPrices(coinIds, {
    enabled: coinIds.length > 0,
    refetchInterval: config.refreshInterval * 1000,
  });

  const { data: searchResults } = useSearchCoins(searchQuery, {
    enabled: searchQuery.length >= 2,
  });

  useCoinsList();

  useEffect(() => {
    if (!config.showClock) return;
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, [config.showClock]);

  // Note: Saving is handled by useIpadWatchlistSync hook

  const getPortfolioCostBasis = useCallback((coinId: string): number | undefined => {
    const token = portfolioTokens.find(t => t.id === coinId);
    if (!token?.positions?.length) return undefined;
    const buys = token.positions.filter(p => p.type === 'buy' || p.type === 'mining' || p.type === 'staking');
    if (!buys.length) return undefined;
    const totalCost = buys.reduce((sum, p) => sum + (p.quantity * p.price), 0);
    const totalQty = buys.reduce((sum, p) => sum + p.quantity, 0);
    return totalQty > 0 ? totalCost / totalQty : undefined;
  }, [portfolioTokens]);

  const addToken = (coin: { id: string; symbol: string; name: string; thumb?: string }) => {
    if (config.tokens.find(t => t.id === coin.id)) return;
    if (config.tokens.length >= 50) { toast.warning('Maximum 50 tokens'); return; }
    const costBasis = getPortfolioCostBasis(coin.id);
    setConfig(prev => ({
      ...prev,
      tokens: [...prev.tokens, { id: coin.id, symbol: coin.symbol.toUpperCase(), name: coin.name, costBasis, image: coin.thumb }],
    }));
    setShowAddToken(false);
    setSearchQuery('');
  };

  const removeToken = (tokenId: string) => {
    setConfig(prev => ({ ...prev, tokens: prev.tokens.filter(t => t.id !== tokenId) }));
  };

  const updateCostBasis = (tokenId: string, costBasis: number | undefined) => {
    setConfig(prev => ({
      ...prev,
      tokens: prev.tokens.map(t => t.id === tokenId ? { ...t, costBasis } : t),
    }));
    setEditingCostBasis(null);
  };

  const syncFromPortfolio = (tokenId: string) => {
    const costBasis = getPortfolioCostBasis(tokenId);
    if (costBasis) updateCostBasis(tokenId, costBasis);
  };

  const addAlert = (tokenId: string, alert: Omit<TokenAlert, 'id'>) => {
    setConfig(prev => ({
      ...prev,
      tokens: prev.tokens.map(t =>
        t.id === tokenId ? { ...t, alerts: [...(t.alerts || []), { ...alert, id: Date.now().toString() }] } : t
      ),
    }));
  };

  const removeAlert = (tokenId: string, alertId: string) => {
    setConfig(prev => ({
      ...prev,
      tokens: prev.tokens.map(t =>
        t.id === tokenId ? { ...t, alerts: (t.alerts || []).filter(a => a.id !== alertId) } : t
      ),
    }));
  };

  const toggleAlert = (tokenId: string, alertId: string) => {
    setConfig(prev => ({
      ...prev,
      tokens: prev.tokens.map(t =>
        t.id === tokenId ? { ...t, alerts: (t.alerts || []).map(a => a.id === alertId ? { ...a, enabled: !a.enabled } : a) } : t
      ),
    }));
  };

  const checkAlertCondition = (alert: TokenAlert, price: number, change24h: number): boolean => {
    if (!alert.enabled) return false;
    switch (alert.type) {
      case 'price_above': return price >= alert.value;
      case 'price_below': return price <= alert.value;
      case 'change_above': return change24h >= alert.value;
      case 'change_below': return change24h <= alert.value;
      default: return false;
    }
  };

  const getActiveAlerts = (token: WatchlistToken, price: number, change24h: number): TokenAlert[] => {
    return (token.alerts || []).filter(a => checkAlertCondition(a, price, change24h));
  };

  const getAlertTypeLabel = (type: AlertConditionType): string => {
    const labels = { price_above: 'Prix ≥', price_below: 'Prix ≤', change_above: 'Var ≥', change_below: 'Var ≤' };
    return labels[type];
  };

  const formatPrice = (price: number): string => {
    if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(6)}`;
  };

  const formatPercent = (value: number): string => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  const getPerformance = (token: WatchlistToken, price: number): { value: number; label: string } => {
    if (token.costBasis && token.costBasis > 0) {
      return { value: ((price - token.costBasis) / token.costBasis) * 100, label: 'vs PRU' };
    }
    return { value: (prices as Prices)[token.id]?.usd_24h_change || 0, label: '24h' };
  };

  const popularCoins = [
    { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin' },
    { id: 'ethereum', symbol: 'ETH', name: 'Ethereum' },
    { id: 'solana', symbol: 'SOL', name: 'Solana' },
    { id: 'ripple', symbol: 'XRP', name: 'XRP' },
    { id: 'cardano', symbol: 'ADA', name: 'Cardano' },
    { id: 'avalanche-2', symbol: 'AVAX', name: 'Avalanche' },
  ];

  // Styles
  const s = {
    page: {
      minHeight: '100vh',
      background: 'var(--bg)',
      padding: '20px',
      position: 'relative' as const,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    clock: {
      fontSize: '42px',
      fontWeight: 300,
      color: 'var(--text)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      letterSpacing: '-2px',
    },
    controls: {
      display: 'flex',
      gap: '10px',
    },
    iconBtn: (isActive: boolean, isHovered: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px',
      background: isActive ? 'rgba(139, 92, 246, 0.15)' : 'var(--card-bg)',
      border: `1px solid ${isActive || isHovered ? 'var(--primary)' : 'var(--border)'}`,
      borderRadius: '10px',
      cursor: 'pointer',
      color: isActive ? 'var(--primary)' : 'var(--muted)',
      transition: 'all 0.2s',
      textDecoration: 'none',
    }),
    addBtn: (isHovered: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 16px',
      background: isHovered ? 'var(--primary)' : 'rgba(139, 92, 246, 0.15)',
      border: '1px solid var(--primary)',
      borderRadius: '10px',
      cursor: 'pointer',
      color: isHovered ? 'white' : 'var(--primary)',
      fontSize: '13px',
      fontWeight: 600,
      transition: 'all 0.2s',
    }),
    settingsPanel: {
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '20px',
    },
    settingsTitle: {
      color: 'var(--text)',
      fontSize: '15px',
      fontWeight: 600,
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    settingsRow: {
      display: 'flex',
      gap: '24px',
      flexWrap: 'wrap' as const,
    },
    settingsGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
    },
    settingsLabel: {
      fontSize: '11px',
      fontWeight: 600,
      color: 'var(--muted)',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    optionBtns: {
      display: 'flex',
      gap: '6px',
    },
    optionBtn: (isActive: boolean) => ({
      padding: '8px 14px',
      background: isActive ? 'var(--primary)' : 'var(--bg)',
      border: `1px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
      borderRadius: '8px',
      color: isActive ? 'white' : 'var(--text)',
      fontSize: '12px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
    select: {
      padding: '8px 12px',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      color: 'var(--text)',
      fontSize: '12px',
      cursor: 'pointer',
    },
    tokenGrid: {
      display: 'grid',
      gap: '14px',
    },
    tokenCard: (isHovered: boolean, hasAlert: boolean, alertColor: string | null) => ({
      background: 'var(--card-bg)',
      border: hasAlert ? `2px solid ${alertColor}` : `1px solid ${isHovered ? 'var(--primary)' : 'rgba(128, 128, 128, 0.15)'}`,
      borderRadius: '14px',
      padding: '18px',
      position: 'relative' as const,
      transition: 'all 0.2s',
    }),
    tokenHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '14px',
    },
    tokenImage: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
    },
    tokenImagePlaceholder: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, var(--primary), #a78bfa)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 700,
      color: 'white',
    },
    tokenSymbol: {
      fontSize: '16px',
      fontWeight: 600,
      color: 'var(--text)',
    },
    tokenName: {
      fontSize: '11px',
      color: 'var(--muted)',
    },
    tokenPrice: {
      fontSize: '26px',
      fontWeight: 400,
      color: 'var(--text)',
      marginBottom: '12px',
      letterSpacing: '-0.5px',
    },
    perfBadge: (isPositive: boolean) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      background: isPositive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 600,
      color: isPositive ? '#10b981' : '#ef4444',
    }),
    perfLabel: {
      fontSize: '10px',
      color: 'var(--muted)',
      marginLeft: '4px',
      fontWeight: 500,
    },
    removeBtn: {
      position: 'absolute' as const,
      top: '10px',
      right: '10px',
      padding: '4px',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: 'var(--muted)',
      opacity: 0.4,
      transition: 'opacity 0.2s',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      color: 'var(--muted)',
      textAlign: 'center' as const,
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '16px',
    },
    pageBtn: (isActive: boolean) => ({
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      background: isActive ? 'var(--primary)' : 'var(--card-bg)',
      border: `1px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
      color: isActive ? 'white' : 'var(--text)',
      fontSize: '14px',
      fontWeight: isActive ? 600 : 400,
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
    modal: {
      position: 'fixed' as const,
      inset: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100,
    },
    modalContent: {
      background: 'var(--bg)',
      borderRadius: '16px',
      width: '90%',
      maxWidth: '460px',
      maxHeight: '85vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column' as const,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      borderBottom: '1px solid var(--border)',
      background: 'var(--card-bg)',
    },
    modalTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '16px',
      fontWeight: 600,
      color: 'var(--text)',
      margin: 0,
    },
    modalBody: {
      flex: 1,
      padding: '20px',
      overflowY: 'auto' as const,
    },
    closeBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      background: 'transparent',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      cursor: 'pointer',
      color: 'var(--muted)',
    },
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 40px',
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '10px',
      fontSize: '14px',
      color: 'var(--text)',
      outline: 'none',
    },
    coinOption: (isDisabled: boolean, isHovered: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      padding: '12px',
      background: isDisabled ? 'rgba(139, 92, 246, 0.08)' : isHovered ? 'var(--card-bg)' : 'transparent',
      border: 'none',
      borderRadius: '10px',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      opacity: isDisabled ? 0.5 : 1,
      textAlign: 'left' as const,
      marginBottom: '4px',
      transition: 'all 0.15s',
    }),
    footer: {
      position: 'fixed' as const,
      bottom: '12px',
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '10px',
      color: 'var(--muted)',
      opacity: 0.5,
    },
  };

  return (
    <div style={s.page}>
      {/* Animations */}
      <style>{`
        @keyframes alertPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .alert-pulse { animation: alertPulse 1s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div style={s.header}>
        {config.showClock && (
          <div style={s.clock}>
            {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        {!config.showClock && <div />}

        <div style={s.controls}>
          <Link
            to="/"
            style={s.iconBtn(false, hoveredBtn === 'home')}
            onMouseEnter={() => setHoveredBtn('home')}
            onMouseLeave={() => setHoveredBtn(null)}
            title="Retour"
          >
            <Icons.Home size={18} />
          </Link>
          <button
            onClick={() => refetch()}
            style={s.iconBtn(false, hoveredBtn === 'refresh')}
            onMouseEnter={() => setHoveredBtn('refresh')}
            onMouseLeave={() => setHoveredBtn(null)}
            title="Rafraîchir"
          >
            <Icons.RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowAddToken(true)}
            style={s.addBtn(hoveredBtn === 'add')}
            onMouseEnter={() => setHoveredBtn('add')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            <Icons.Plus size={16} />
            Ajouter
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            style={s.iconBtn(showSettings, hoveredBtn === 'settings')}
            onMouseEnter={() => setHoveredBtn('settings')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            <Icons.Settings size={18} />
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div style={s.settingsPanel}>
          <div style={s.settingsTitle}>
            <Icons.Settings size={16} />
            Paramètres
          </div>
          <div style={s.settingsRow}>
            <div style={s.settingsGroup}>
              <span style={s.settingsLabel}>Colonnes</span>
              <div style={s.optionBtns}>
                {([2, 3, 4] as const).map(cols => (
                  <button key={cols} onClick={() => setConfig(p => ({ ...p, gridCols: cols }))} style={s.optionBtn(config.gridCols === cols)}>
                    {cols}
                  </button>
                ))}
              </div>
            </div>
            <div style={s.settingsGroup}>
              <span style={s.settingsLabel}>Lignes/page</span>
              <div style={s.optionBtns}>
                {([2, 3, 4] as const).map(rows => (
                  <button key={rows} onClick={() => { setConfig(p => ({ ...p, rowsPerPage: rows })); setCurrentPage(0); }} style={s.optionBtn(config.rowsPerPage === rows)}>
                    {rows}
                  </button>
                ))}
              </div>
            </div>
            <div style={s.settingsGroup}>
              <span style={s.settingsLabel}>Rafraîchissement</span>
              <select aria-label="Rafraîchissement" value={config.refreshInterval} onChange={e => setConfig(p => ({ ...p, refreshInterval: parseInt(e.target.value) }))} style={s.select}>
                <option value={15}>15s</option>
                <option value={30}>30s</option>
                <option value={60}>1 min</option>
                <option value={300}>5 min</option>
              </select>
            </div>
            <div style={s.settingsGroup}>
              <span style={s.settingsLabel}>Horloge</span>
              <button onClick={() => setConfig(p => ({ ...p, showClock: !p.showClock }))} style={s.optionBtn(config.showClock)}>
                {config.showClock ? 'Activée' : 'Désactivée'}
              </button>
            </div>
          </div>

          {/* Portfolio import */}
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                const newTokens = portfolioTokens.map(t => ({
                  id: t.id, symbol: t.displayName, name: t.displayName, costBasis: getPortfolioCostBasis(t.id),
                }));
                setConfig(p => ({ ...p, tokens: newTokens }));
              }}
              disabled={!portfolioTokens.length}
              style={{
                ...s.optionBtn(false),
                opacity: portfolioTokens.length ? 1 : 0.4,
                cursor: portfolioTokens.length ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <Icons.Download size={14} />
              Importer portfolio ({portfolioTokens.length})
            </button>
            <button
              onClick={() => setConfig(p => ({ ...p, tokens: [] }))}
              disabled={!config.tokens.length}
              style={{
                ...s.optionBtn(false),
                opacity: config.tokens.length ? 1 : 0.4,
                cursor: config.tokens.length ? 'pointer' : 'not-allowed',
                color: config.tokens.length ? '#ef4444' : 'var(--muted)',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <Icons.Trash2 size={14} />
              Tout supprimer
            </button>
          </div>
        </div>
      )}

      {/* Token Grid */}
      {isLoadingConfig ? (
        <div style={s.emptyState}>
          <Icons.RefreshCw size={40} className="animate-spin" style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p style={{ fontSize: '16px', marginBottom: '6px' }}>Chargement...</p>
        </div>
      ) : config.tokens.length === 0 ? (
        <div style={s.emptyState}>
          <Icons.Eye size={40} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p style={{ fontSize: '16px', marginBottom: '6px' }}>Aucun token surveillé</p>
          <p style={{ fontSize: '13px', opacity: 0.6 }}>Cliquez sur "Ajouter" pour commencer</p>
        </div>
      ) : (
        <>
          {totalPages > 1 && (
            <div style={s.pagination}>
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button key={idx} onClick={() => setCurrentPage(idx)} style={s.pageBtn(currentPage === idx)}>
                  {idx + 1}
                </button>
              ))}
              <span style={{ display: 'flex', alignItems: 'center', fontSize: '11px', color: 'var(--muted)', marginLeft: '8px' }}>
                {config.tokens.length} tokens
              </span>
            </div>
          )}

          <div style={{ ...s.tokenGrid, gridTemplateColumns: `repeat(${config.gridCols}, 1fr)` }}>
            {paginatedTokens.map(token => {
              const price = (prices as Prices)[token.id]?.usd || 0;
              const change24h = (prices as Prices)[token.id]?.usd_24h_change || 0;
              const performance = getPerformance(token, price);
              const isPositive = performance.value >= 0;
              const activeAlerts = getActiveAlerts(token, price, change24h);
              const hasActiveAlerts = activeAlerts.length > 0;
              const alertColor = hasActiveAlerts ? ALERT_COLORS.find(c => c.id === activeAlerts[0].colorId)?.color || '#fff' : null;
              const sparklineData = generateSparklineData(change24h, token.id);
              const sparklineColor = isPositive ? '#10b981' : '#ef4444';

              return (
                <div
                  key={token.id}
                  style={{ ...s.tokenCard(hoveredToken === token.id, hasActiveAlerts, alertColor), overflow: 'hidden' }}
                  onMouseEnter={() => setHoveredToken(token.id)}
                  onMouseLeave={() => setHoveredToken(null)}
                >
                  {/* Background Sparkline */}
                  <MiniSparkline data={sparklineData} color={sparklineColor} opacity={0.25} id={token.id} />

                  {/* Alert indicators */}
                  {hasActiveAlerts && (
                    <div style={{ position: 'absolute', top: '10px', left: '10px', display: 'flex', gap: '4px' }}>
                      {activeAlerts.map(alert => (
                        <div
                          key={alert.id}
                          className="alert-pulse"
                          style={{
                            width: '10px', height: '10px', borderRadius: '50%',
                            backgroundColor: ALERT_COLORS.find(c => c.id === alert.colorId)?.color || '#fff',
                          }}
                          title={`${getAlertTypeLabel(alert.type)} ${alert.type.includes('change') ? `${alert.value}%` : `$${alert.value}`}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Remove button */}
                  <button
                    onClick={() => removeToken(token.id)}
                    style={s.removeBtn}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '0.4')}
                  >
                    <Icons.X size={14} />
                  </button>

                  {/* Token header */}
                  <div style={s.tokenHeader}>
                    {token.image && (
                      <img src={token.image} alt={token.symbol} style={s.tokenImage} />
                    )}
                    <div>
                      <div style={s.tokenSymbol}>{token.symbol}</div>
                      <div style={s.tokenName}>{token.name}</div>
                    </div>
                  </div>

                  {/* Price */}
                  <div style={s.tokenPrice}>{isLoading && !price ? '...' : formatPrice(price)}</div>

                  {/* Performance + actions */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={s.perfBadge(isPositive)}>
                      {isPositive ? <Icons.TrendingUp size={14} /> : <Icons.TrendingDown size={14} />}
                      {formatPercent(performance.value)}
                      <span style={s.perfLabel}>{performance.label}</span>
                    </div>

                    {/* Actions */}
                    {editingCostBasis === token.id ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input
                          type="number"
                          value={tempCostBasis}
                          onChange={e => setTempCostBasis(e.target.value)}
                          placeholder="PRU"
                          style={{ width: '70px', padding: '6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontSize: '11px' }}
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === 'Enter') updateCostBasis(token.id, parseFloat(tempCostBasis) || undefined);
                            if (e.key === 'Escape') setEditingCostBasis(null);
                          }}
                        />
                        <button onClick={() => updateCostBasis(token.id, parseFloat(tempCostBasis) || undefined)} style={{ padding: '6px', background: '#10b981', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'white' }}>
                          <Icons.Check size={12} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {token.costBasis && <span style={{ fontSize: '10px', color: 'var(--muted)' }}>PRU: ${token.costBasis.toFixed(2)}</span>}
                        <button onClick={() => { setTempCostBasis(token.costBasis?.toString() || ''); setEditingCostBasis(token.id); }} style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--muted)', opacity: 0.5 }} title="Modifier PRU">
                          <Icons.Edit size={12} />
                        </button>
                        {getPortfolioCostBasis(token.id) && (
                          <button onClick={() => syncFromPortfolio(token.id)} style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', opacity: 0.6 }} title="Sync portfolio">
                            <Icons.RefreshCw size={12} />
                          </button>
                        )}
                        <button
                          onClick={() => setEditingAlerts(token.id)}
                          style={{
                            padding: '4px', background: (token.alerts?.length || 0) > 0 ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                            border: 'none', borderRadius: '4px', cursor: 'pointer',
                            color: (token.alerts?.length || 0) > 0 ? 'var(--primary)' : 'var(--muted)',
                            display: 'flex', alignItems: 'center', gap: '2px', opacity: (token.alerts?.length || 0) > 0 ? 1 : 0.5,
                          }}
                          title="Alertes"
                        >
                          <Icons.Bell size={12} />
                          {(token.alerts?.length || 0) > 0 && <span style={{ fontSize: '9px' }}>{token.alerts?.length}</span>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Add Token Modal */}
      {showAddToken && (
        <div style={s.modal} onClick={() => setShowAddToken(false)}>
          <div style={s.modalContent} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}><Icons.Plus size={18} /> Ajouter un token</h3>
              <button style={s.closeBtn} onClick={() => setShowAddToken(false)}><Icons.X size={18} /></button>
            </div>
            <div style={s.modalBody}>
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <Icons.Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                <input type="text" placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={s.searchInput} autoFocus />
              </div>

              {searchQuery.length >= 2 && searchResults?.coins ? (
                <>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '10px', textTransform: 'uppercase' }}>Résultats</div>
                  {searchResults.coins.slice(0, 8).map((coin: any) => {
                    const isAdded = config.tokens.some(t => t.id === coin.id);
                    return (
                      <button
                        key={coin.id}
                        onClick={() => addToken({ id: coin.id, symbol: coin.symbol, name: coin.name, thumb: coin.thumb })}
                        disabled={isAdded}
                        style={s.coinOption(isAdded, hoveredToken === coin.id)}
                        onMouseEnter={() => setHoveredToken(coin.id)}
                        onMouseLeave={() => setHoveredToken(null)}
                      >
                        {coin.thumb && <img src={coin.thumb} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--text)', fontWeight: 500, fontSize: '13px' }}>{coin.name}</div>
                          <div style={{ color: 'var(--muted)', fontSize: '11px' }}>{coin.symbol.toUpperCase()}</div>
                        </div>
                        {isAdded && <Icons.Check size={16} style={{ color: 'var(--primary)' }} />}
                      </button>
                    );
                  })}
                </>
              ) : (
                <>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '10px', textTransform: 'uppercase' }}>Populaires</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '16px' }}>
                    {popularCoins.map(coin => {
                      const isAdded = config.tokens.some(t => t.id === coin.id);
                      return (
                        <button key={coin.id} onClick={() => addToken(coin)} disabled={isAdded} style={{ ...s.coinOption(isAdded, false), padding: '10px' }}>
                          <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), #a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: 'white' }}>
                            {coin.symbol.slice(0, 2)}
                          </div>
                          <span style={{ color: 'var(--text)', fontSize: '13px', fontWeight: 500 }}>{coin.symbol}</span>
                        </button>
                      );
                    })}
                  </div>

                  {portfolioTokens.length > 0 && (
                    <>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '10px', textTransform: 'uppercase' }}>Mon portfolio</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        {portfolioTokens.map(token => {
                          const isAdded = config.tokens.some(t => t.id === token.id);
                          return (
                            <button key={token.id} onClick={() => addToken({ id: token.id, symbol: token.displayName, name: token.displayName })} disabled={isAdded} style={{ ...s.coinOption(isAdded, false), padding: '10px' }}>
                              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: 'white' }}>
                                {token.displayName.slice(0, 2)}
                              </div>
                              <span style={{ color: 'var(--text)', fontSize: '13px', fontWeight: 500 }}>{token.displayName}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alert Configuration Modal */}
      {editingAlerts && (() => {
        const token = config.tokens.find(t => t.id === editingAlerts);
        if (!token) return null;
        const price = (prices as Prices)[token.id]?.usd || 0;
        const change24h = (prices as Prices)[token.id]?.usd_24h_change || 0;

        return (
          <div style={s.modal} onClick={() => setEditingAlerts(null)}>
            <div style={{ ...s.modalContent, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <h3 style={s.modalTitle}>
                  <Icons.Bell size={18} />
                  Alertes - {token.symbol}
                </h3>
                <button style={s.closeBtn} onClick={() => setEditingAlerts(null)}><Icons.X size={18} /></button>
              </div>
              <div style={{ padding: '12px 20px', background: 'var(--card-bg)', borderBottom: '1px solid var(--border)', fontSize: '12px', color: 'var(--muted)' }}>
                Prix: <span style={{ color: 'var(--text)', fontWeight: 600 }}>${price.toFixed(4)}</span>
                <span style={{ margin: '0 12px' }}>•</span>
                Var 24h: <span style={{ color: change24h >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>{formatPercent(change24h)}</span>
              </div>
              <div style={s.modalBody}>
                {(token.alerts?.length || 0) === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: 'var(--muted)' }}>
                    <Icons.Bell size={28} style={{ opacity: 0.3, marginBottom: '10px' }} />
                    <p style={{ fontSize: '13px' }}>Aucune alerte configurée</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    {token.alerts?.map(alert => {
                      const alertColor = ALERT_COLORS.find(c => c.id === alert.colorId);
                      const isActive = checkAlertCondition(alert, price, change24h);
                      return (
                        <div key={alert.id} style={{
                          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                          background: isActive ? 'rgba(139, 92, 246, 0.1)' : 'var(--card-bg)',
                          border: `1px solid ${isActive ? alertColor?.color : 'var(--border)'}`,
                          borderRadius: '10px',
                        }}>
                          <div className={isActive ? 'alert-pulse' : ''} style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: alertColor?.color || '#fff', flexShrink: 0 }} />
                          <div style={{ flex: 1, fontSize: '13px', color: 'var(--text)' }}>
                            {getAlertTypeLabel(alert.type)} {alert.type.includes('change') ? `${alert.value}%` : `$${alert.value}`}
                            {isActive && <span style={{ marginLeft: '8px', padding: '2px 6px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontSize: '9px', borderRadius: '4px', fontWeight: 600 }}>ACTIF</span>}
                          </div>
                          <button onClick={() => toggleAlert(token.id, alert.id)} style={{ padding: '4px 10px', background: alert.enabled ? 'rgba(16, 185, 129, 0.15)' : 'var(--bg)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: alert.enabled ? '#10b981' : 'var(--muted)', fontSize: '11px', fontWeight: 600 }}>
                            {alert.enabled ? 'ON' : 'OFF'}
                          </button>
                          <button onClick={() => removeAlert(token.id, alert.id)} style={{ padding: '4px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#ef4444' }}>
                            <Icons.Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add alert form */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '10px', textTransform: 'uppercase' }}>Nouvelle alerte</div>
                  <InlineAlertForm onAdd={alert => addAlert(token.id, alert)} currentPrice={price} />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer */}
      <div style={s.footer}>
        Rafraîchissement: {config.refreshInterval}s
        {isAuthenticated && (
          <span style={{ marginLeft: '12px' }}>
            {isSyncing ? (
              <span style={{ color: 'var(--primary)' }}>• Sync...</span>
            ) : syncError ? (
              <span style={{ color: '#ef4444' }} title={syncError}>• Erreur sync</span>
            ) : lastSyncedAt ? (
              <span style={{ color: '#10b981' }}>• Synchronisé</span>
            ) : null}
          </span>
        )}
      </div>
    </div>
  );
}

// Inline Alert Form Component
function InlineAlertForm({ onAdd, currentPrice }: { onAdd: (alert: Omit<TokenAlert, 'id'>) => void; currentPrice: number }) {
  const [type, setType] = useState<AlertConditionType>('price_above');
  const [value, setValue] = useState('');
  const [colorId, setColorId] = useState('white');
  const [hoveredType, setHoveredType] = useState<string | null>(null);

  const handleSubmit = () => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    onAdd({ type, value: numValue, colorId, enabled: true });
    setValue('');
  };

  const types = [
    { id: 'price_above', label: 'Prix ≥', icon: <Icons.TrendingUp size={14} /> },
    { id: 'price_below', label: 'Prix ≤', icon: <Icons.TrendingDown size={14} /> },
    { id: 'change_above', label: 'Var ≥', icon: <Icons.ArrowUpCircle size={14} /> },
    { id: 'change_below', label: 'Var ≤', icon: <Icons.ArrowDownCircle size={14} /> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
        {types.map(t => (
          <button
            key={t.id}
            onClick={() => setType(t.id as AlertConditionType)}
            onMouseEnter={() => setHoveredType(t.id)}
            onMouseLeave={() => setHoveredType(null)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '10px 6px',
              background: type === t.id ? 'rgba(139, 92, 246, 0.15)' : hoveredType === t.id ? 'var(--card-bg)' : 'var(--bg)',
              border: `1px solid ${type === t.id ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: '8px', cursor: 'pointer', color: type === t.id ? 'var(--primary)' : 'var(--text)', fontSize: '10px', fontWeight: 600, transition: 'all 0.2s',
            }}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <input
          type="number"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={type.includes('change') ? 'Ex: -10' : `Ex: ${currentPrice.toFixed(2)}`}
          style={{ flex: 1, padding: '10px 12px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '13px', outline: 'none' }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        />
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {ALERT_COLORS.map(c => (
            <button
              key={c.id}
              onClick={() => setColorId(c.id)}
              style={{
                width: '24px', height: '24px', borderRadius: '50%', backgroundColor: c.color,
                border: colorId === c.id ? '2px solid white' : '2px solid transparent',
                cursor: 'pointer', transition: 'transform 0.2s', transform: colorId === c.id ? 'scale(1.1)' : 'scale(1)',
              }}
              title={c.label}
            />
          ))}
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!value}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '12px',
          background: value ? 'var(--primary)' : 'var(--card-bg)', border: 'none', borderRadius: '10px',
          color: value ? 'white' : 'var(--muted)', fontSize: '13px', fontWeight: 600, cursor: value ? 'pointer' : 'not-allowed', transition: 'all 0.2s',
        }}
      >
        <Icons.Plus size={16} />
        Ajouter l'alerte
      </button>
    </div>
  );
}

export default DashboardIpad;
